import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { showModal } from 'react-redux-modal-provider';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import TrafficTimeService from '@app/services/trafficTime.service';
import TablePaginated from '@app/components/table-paginated/TablePaginated';
import TrafficTimeCard from '@app/components/card/TrafficTimeCard';
import ModalTrafficTime from '@app/modals/trafficTime/ModalTrafficTime';
import ModalTrafficTimeHistorial from '@app/modals/trafficTime/ModalTrafficTimeHistorial';
import ModalNotifyEmail from '@app/modals/notifications/ModalNotifyEmail';
import UserNotifyEmailService from '@app/services/userNotifyEmail.service';
import { Button, ModalConfirm } from '@app/components/index';
import Dropdown from 'react-bootstrap/Dropdown';

const TIPO_GERENCIA = 'Estado-Ticket-Rojo-Gerencia';

const formatFecha = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const SEGMENTOS_ORDEN = ['SVV', 'SGE', 'TETRA'];
const GRAVEDAD_ORDEN = { CRITICA: 0, GRAVE: 1, LEVE: 2, BAJA: 3 };

const SEGMENTO_STYLES = {
    SVV:   { color: '#6f42c1', icon: 'fas fa-video' },
    SGE:   { color: '#007bff', icon: 'fas fa-broadcast-tower' },
    TETRA: { color: '#20c997', icon: 'fas fa-satellite-dish' }
};

const TrafficTimePage = ({ user }) => {
    const [items, setItems] = useState([]);
    const [historial, setHistorial] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showHistorial, setShowHistorial] = useState(false);
    const [showTabla, setShowTabla] = useState(false);
    const [gerenciaEmails, setGerenciaEmails] = useState([]);
    const [showGerenciaHist, setShowGerenciaHist] = useState(false);
    const navigate = useNavigate();

    const actualizarGerencia = useCallback(() => {
        UserNotifyEmailService.getAll()
            .then((resp) => {
                let list = [];
                if (Array.isArray(resp?.data)) list = resp.data;
                else if (Array.isArray(resp)) list = resp;
                const parsed = list.filter((r) => r.tipo_uxne === TIPO_GERENCIA)
                    .map((r) => ({
                        ...r,
                        emailStr_uxne: Array.isArray(r.emailStr_uxne)
                            ? r.emailStr_uxne
                            : (() => { try { return JSON.parse(r.emailStr_uxne); } catch (_) { return []; } })()
                    }))
                    .sort((a, b) => b.id_uxne - a.id_uxne);
                setGerenciaEmails(parsed);
            })
            .catch(() => setGerenciaEmails([]));
    }, []);

    const actualizar = useCallback(() => {
        setIsLoading(true);
        Promise.all([
            TrafficTimeService.getAll(),
            TrafficTimeService.getHistorial()
        ])
            .then(([activos, hist]) => {
                setItems(activos || []);
                setHistorial(hist || []);
            })
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        actualizar();
        actualizarGerencia();
    }, [actualizar, actualizarGerencia]);

    useEffect(() => {
        if (user && user.id_p > 9999) {
            navigate('/');
        }
    }, [user, navigate]);

    const onOpenEditar = (item) => {
        showModal(ModalTrafficTime, { item, onGuardar: actualizar });
    };

    const onOpenHistorialItem = (item) => {
        showModal(ModalTrafficTimeHistorial, { item });
    };

    const onEliminar = (item) => {
        showModal(ModalConfirm, {
            title: 'Eliminar Traffic Time',
            message: `¿Confirma eliminar la configuracion ${item.segmento_tt} / ${item.gravedad_tt}?`,
            onConfirm: () => {
                TrafficTimeService.destroy(item.id_tt).then(() => actualizar());
            }
        });
    };

    const gerenciaActivo = useMemo(
        () => gerenciaEmails.find((r) => Boolean(r.emailBool_uxne)),
        [gerenciaEmails]
    );
    const gerenciaEmailsActivos = gerenciaActivo?.emailStr_uxne ?? [];

    const onEditarGerencia = () => {
        showModal(ModalNotifyEmail, {
            tipo: TIPO_GERENCIA,
            emailsActivos: gerenciaEmailsActivos,
            onGuardar: actualizarGerencia
        });
    };

    const gruposPorSegmento = useMemo(() => {
        const grupos = {};
        items.forEach((it) => {
            const seg = it.segmento_tt || 'SIN SEGMENTO';
            if (!grupos[seg]) grupos[seg] = [];
            grupos[seg].push(it);
        });
        Object.keys(grupos).forEach((seg) => {
            grupos[seg].sort(
                (a, b) =>
                    (GRAVEDAD_ORDEN[a.gravedad_tt] ?? 99) -
                    (GRAVEDAD_ORDEN[b.gravedad_tt] ?? 99)
            );
        });
        const ordenados = SEGMENTOS_ORDEN.filter((s) => grupos[s]).map((s) => [s, grupos[s]]);
        const otros = Object.keys(grupos)
            .filter((s) => !SEGMENTOS_ORDEN.includes(s))
            .map((s) => [s, grupos[s]]);
        return [...ordenados, ...otros];
    }, [items]);

    const columns = React.useMemo(
        () => [
            { Header: '#', accessor: 'id_tt', width: 60 },
            { Header: 'Segmento', accessor: 'segmento_tt' },
            { Header: 'Gravedad', accessor: 'gravedad_tt' },
            {
                Header: 'Verde Cap.',
                accessor: 'nivelVerdeHoursCapital_tt',
                Cell: ({ value }) => (
                    <span className="badge" style={{ backgroundColor: '#28a745', color: '#fff' }}>{value ?? '-'}</span>
                )
            },
            {
                Header: 'Amar. Cap.',
                accessor: 'nivelAmarilloHoursCapital_tt',
                Cell: ({ value }) => (
                    <span className="badge" style={{ backgroundColor: '#ffc107', color: '#212529' }}>{value ?? '-'}</span>
                )
            },
            {
                Header: 'Nar. Cap.',
                accessor: 'nivelNaranjaHoursCapital_tt',
                Cell: ({ value }) => (
                    <span className="badge" style={{ backgroundColor: '#fd7e14', color: '#fff' }}>{value ?? '-'}</span>
                )
            },
            {
                Header: 'Rojo Cap.',
                accessor: 'nivelRojoHoursCapital_tt',
                Cell: ({ value }) => (
                    <span className="badge" style={{ backgroundColor: '#dc3545', color: '#fff' }}>{value ?? '-'}</span>
                )
            },
            {
                Header: 'Verde Int.',
                accessor: 'nivelVerdeHours_tt',
                Cell: ({ value }) => (
                    <span className="badge" style={{ backgroundColor: '#28a745', color: '#fff' }}>{value ?? '-'}</span>
                )
            },
            {
                Header: 'Amar. Int.',
                accessor: 'nivelAmarilloHours_tt',
                Cell: ({ value }) => (
                    <span className="badge" style={{ backgroundColor: '#ffc107', color: '#212529' }}>{value ?? '-'}</span>
                )
            },
            {
                Header: 'Nar. Int.',
                accessor: 'nivelNaranjaHours_tt',
                Cell: ({ value }) => (
                    <span className="badge" style={{ backgroundColor: '#fd7e14', color: '#fff' }}>{value ?? '-'}</span>
                )
            },
            {
                Header: 'Rojo Int.',
                accessor: 'nivelRojoHours_tt',
                Cell: ({ value }) => (
                    <span className="badge" style={{ backgroundColor: '#dc3545', color: '#fff' }}>{value ?? '-'}</span>
                )
            },
            {
                Header: 'Estado',
                accessor: 'isDeleted_tt',
                Cell: ({ value }) =>
                    value
                        ? <span className="badge badge-secondary">HISTORICO</span>
                        : <span className="badge badge-success">ACTIVO</span>
            },
            {
                Header: 'Creado por',
                accessor: 'creadoPor.nombre_p',
                Cell: ({ value }) => value
                    ? (
                        <span>
                            <i className="fas fa-user mr-1 text-muted" />
                            {value}
                        </span>
                    )
                    : <span className="text-muted">-</span>
            },
            {
                width: 10,
                Header: '',
                accessor: 'id',
                disableSortBy: true,
                Filter: false,
                Cell: ({ row }) => {
                    const item = row.original;
                    if (item.isDeleted_tt) return null;
                    return (
                        <Dropdown size="sm">
                            <Dropdown.Toggle variant="danger" id={`dd-tt-${item.id_tt}`} />
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => onOpenEditar(item)}>
                                    Editar
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => onOpenHistorialItem(item)}>
                                    Ver historial
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => onEliminar(item)}>
                                    Eliminar
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    );
                }
            }
        ],
        []
    );

    const dataTabla = showHistorial ? historial : items;

    return (
        <div className="container-fluid py-3">
            {/* Cabecera */}
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3">
                <div>
                    <h3 className="mb-1">
                        <i className="fas fa-traffic-light text-warning mr-2" />
                        Traffic Time
                    </h3>
                    <small className="text-muted">
                        Semaforo de tiempos por segmento y gravedad. Cada nuevo registro con el mismo
                        segmento y gravedad envia al historico al anterior.
                    </small>
                </div>
                <div className="d-flex align-items-center mt-3 mt-lg-0">
                    <Button
                        theme="primary"
                        icon="fas fa-plus"
                        onClick={() => onOpenEditar()}
                    >
                      Nuevo Traffic Time
                    </Button>
                </div>
            </div>

            {/* Card: Notificacion Estado Rojo - Gerencia */}
            <div className="row mb-4">
            <div className="col-12 col-lg-4">
            <div className="card shadow-sm border-0 h-100" style={{ borderLeft: '4px solid #dc3545' }}>
                <div className="card-body py-2 px-3">
                    <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 8 }}>
                        {/* Icono + titulo + emails */}
                        <div className="d-flex align-items-center" style={{ gap: 10, flex: 1, minWidth: 0 }}>
                            <span
                                className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: '#dc3545', color: '#fff', fontSize: 13 }}
                            >
                                <i className="fas fa-bell" />
                            </span>
                            <div style={{ minWidth: 0 }}>
                                <div className="d-flex align-items-center" style={{ gap: 6 }}>
                                    <span className="font-weight-bold" style={{ color: '#dc3545', fontSize: 13, letterSpacing: '.4px' }}>
                                        NOTIF. ESTADO ROJO - GERENCIA
                                    </span>
                                    <span className={`badge ${gerenciaActivo ? 'badge-success' : 'badge-secondary'}`} style={{ fontSize: 10 }}>
                                        {gerenciaActivo ? 'ACTIVO' : 'SIN CONFIG.'}
                                    </span>
                                </div>
                                <div className="d-flex flex-wrap align-items-center mt-1" style={{ gap: 4 }}>
                                    {gerenciaEmailsActivos.length > 0
                                        ? gerenciaEmailsActivos.map((email) => (
                                            <span
                                                key={email}
                                                style={{
                                                    fontSize: 11, padding: '1px 7px', borderRadius: 3,
                                                    backgroundColor: '#e9ecef', color: '#495057',
                                                    border: '1px solid #ced4da'
                                                }}
                                            >
                                                {email}
                                            </span>
                                        ))
                                        : <span className="text-muted" style={{ fontSize: 11, fontStyle: 'italic' }}>Sin correos configurados</span>
                                    }
                                </div>
                            </div>
                        </div>
                        {/* Botones */}
                        <div className="d-flex align-items-center flex-shrink-0" style={{ gap: 6 }}>
                            <button
                                type="button"
                                className="btn btn-sm"
                                style={{
                                    backgroundColor: '#6c757d', borderColor: '#6c757d', color: '#fff',
                                    width: 32, height: 32, borderRadius: '50%', padding: 0,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    position: 'relative'
                                }}
                                onClick={() => setShowGerenciaHist((v) => !v)}
                                disabled={gerenciaEmails.length === 0}
                                title={showGerenciaHist ? 'Ocultar historial' : 'Ver historial'}
                            >
                                <i className={`fas ${showGerenciaHist ? 'fa-chevron-up' : 'fa-history'}`} />
                                {gerenciaEmails.length > 0 && (
                                    <span
                                        style={{
                                            position: 'absolute', top: -5, right: -5,
                                            backgroundColor: '#343a40', color: '#fff',
                                            fontSize: 9, fontWeight: 700,
                                            width: 16, height: 16, borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            lineHeight: 1
                                        }}
                                    >
                                        {gerenciaEmails.length}
                                    </span>
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                style={{
                                    width: 32, height: 32, borderRadius: '50%', padding: 0,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                                }}
                                onClick={onEditarGerencia}
                                title="Editar correos"
                            >
                                <i className="fas fa-pen" />
                            </button>
                        </div>
                    </div>

                    {/* Historial desplegable */}
                    {showGerenciaHist && (
                        <div className="mt-2" style={{ border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ padding: '6px 12px', backgroundColor: '#fdecea', borderBottom: '1px solid #f5c6cb', color: '#721c24', fontSize: 11, fontWeight: 600 }}>
                                <i className="fas fa-history mr-1" />
                                Historial de configuraciones
                            </div>
                            {gerenciaEmails.length === 0 ? (
                                <div className="text-muted text-center" style={{ padding: '10px', fontSize: 12 }}>Sin historial registrado.</div>
                            ) : (
                                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                                    {gerenciaEmails.map((rec, idx) => (
                                        <div
                                            key={rec.id_uxne}
                                            style={{
                                                padding: '7px 12px',
                                                borderBottom: idx < gerenciaEmails.length - 1 ? '1px solid #f3f4f6' : 'none',
                                                backgroundColor: rec.emailBool_uxne ? '#f0fdf4' : '#fafafa'
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 4 }}>
                                                <span style={{ fontSize: 11, color: '#6c757d' }}>
                                                    {formatFecha(rec.createdAt)}
                                                    {rec.creadoPorEmail?.nombre_p && (
                                                        <>
                                                            {' • '}
                                                            <strong>{rec.creadoPorEmail.nombre_p}</strong>
                                                        </>
                                                    )}
                                                </span>
                                                <span className={`badge ${rec.emailBool_uxne ? 'badge-success' : 'badge-secondary'}`} style={{ fontSize: 10 }}>
                                                    {rec.emailBool_uxne ? 'ACTIVO' : 'INACTIVO'}
                                                </span>
                                            </div>
                                            <div className="d-flex flex-wrap" style={{ gap: 4 }}>
                                                {(rec.emailStr_uxne ?? []).length > 0
                                                    ? rec.emailStr_uxne.map((email) => (
                                                        <span key={email} style={{ fontSize: 11, padding: '1px 7px', borderRadius: 3, backgroundColor: '#e9ecef', color: '#495057' }}>
                                                            {email}
                                                        </span>
                                                    ))
                                                    : <span className="text-muted" style={{ fontSize: 11 }}>Sin correos</span>
                                                }
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            </div>
            </div>

            {/* Cards agrupadas por segmento */}
            {!showHistorial && (
                <div className="mb-4">
                    {isLoading && items.length === 0 && (
                        <div className="text-center text-muted p-3">
                            <span className="spinner-border spinner-border-sm mr-2" />
                            Cargando...
                        </div>
                    )}
                    {!isLoading && items.length === 0 && (
                        <div className="alert alert-info d-flex align-items-center">
                            <i className="fas fa-info-circle mr-2" />
                            {'No hay configuraciones activas. Presione '}
                            <strong className="mx-1">NUEVO</strong>
                            {' para crear una.'}
                        </div>
                    )}
                    <div className="row">
                        {gruposPorSegmento.map(([segmento, arr]) => {
                            const style = SEGMENTO_STYLES[segmento] || { color: '#6c757d', icon: 'fas fa-layer-group' };
                            return (
                                <div key={segmento} className="col-12 col-xl-4 mb-3">
                                    <div className="card shadow-sm h-100 border-0">
                                        <div
                                            className="card-header d-flex justify-content-between align-items-center"
                                            style={{
                                                background: `linear-gradient(90deg, ${style.color}22 0%, #ffffff 100%)`,
                                                borderBottom: `2px solid ${style.color}`
                                            }}
                                        >
                                            <div className="d-flex align-items-center">
                                                <span
                                                    className="d-inline-flex align-items-center justify-content-center mr-2"
                                                    style={{
                                                        width: 34,
                                                        height: 34,
                                                        borderRadius: '50%',
                                                        backgroundColor: style.color,
                                                        color: '#fff'
                                                    }}
                                                >
                                                    <i className={style.icon} />
                                                </span>
                                                <div>
                                                    <div className="font-weight-bold" style={{ color: style.color, letterSpacing: '.5px' }}>
                                                        {segmento}
                                                    </div>
                                                    <small className="text-muted">
                                                        {arr.length}
                                                        {arr.length === 1 ? ' gravedad configurada' : ' gravedades configuradas'}
                                                    </small>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                title="Agregar gravedad a este segmento"
                                                onClick={() => onOpenEditar({
                                                    id_tt: 0,
                                                    segmento_tt: segmento,
                                                    gravedad_tt: '',
                                                    nivelVerdeHoursCapital_tt: '',
                                                    nivelAmarilloHoursCapital_tt: '',
                                                    nivelNaranjaHoursCapital_tt: '',
                                                    nivelRojoHoursCapital_tt: '',
                                                    nivelVerdeHours_tt: '',
                                                    nivelAmarilloHours_tt: '',
                                                    nivelNaranjaHours_tt: '',
                                                    nivelRojoHours_tt: ''
                                                })}
                                            >
                                                <i className="fas fa-plus" />
                                            </button>
                                        </div>
                                        <div className="card-body">
                                            {arr.map((it) => (
                                                <div key={it.id_tt} className="mb-2">
                                                    <TrafficTimeCard
                                                        item={it}
                                                        onEditar={onOpenEditar}
                                                        onEliminar={onEliminar}
                                                        onVerHistorial={onOpenHistorialItem}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Toggle tabla */}
            <div className="card shadow-sm mb-2 border-0">
                <div className="card-body py-2 d-flex justify-content-between align-items-center flex-wrap">
                    <div>
                        <h5 className="mb-0">
                            <i className="fas fa-table text-secondary mr-2" />
                            {showHistorial ? 'Historial completo' : 'Tabla de configuraciones activas'}
                        </h5>
                        <small className="text-muted">
                            Vista tabular con busqueda, filtros y paginacion.
                        </small>
                    </div>
                    <Form.Check
                        type="switch"
                        id="switch-mostrar-tabla"
                        label={showTabla ? 'Ocultar tabla' : 'Mostrar tabla'}
                        checked={showTabla}
                        onChange={(e) => setShowTabla(e.target.checked)}
                    />
                </div>
            </div>

            {showTabla && (
                <div className="card shadow-sm border-0">
                    <div className="card-body">
                        <TablePaginated columns={columns} data={dataTabla} />
                    </div>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state.auth.currentUser
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(TrafficTimePage);
